import { createContext, useContext, useEffect, useState } from "react";
import { NextComponentType, NextPageContext } from "next";
import { AppContext } from "next/app";
import { useRouter } from "next/router";
import { getDataLayer } from "utils/analytics";
import { OPTIMIZE_EXPERIMENTS, OPTIMIZE_BUCKETS } from "utils/abTesting/config";
import { AbTestData, ExperimentName } from "./types";
import {
  appendVaryHeaders,
  determineActiveTest,
  getBucketsForExperiments,
} from "./utils";

export interface AbTestProps {
  abTest?: AbTestData;
}

export type AbTestNextPageContext = NextPageContext & AbTestProps;
export type AbTestAppContext = AppContext & AbTestProps;
export type AbTestAppPageContext = AbTestNextPageContext | AbTestAppContext;

const AbTestContext = createContext<AbTestProps>({});

function getNextPageContext(ctx: AbTestAppPageContext): AbTestNextPageContext {
  return "ctx" in ctx ? ctx.ctx : ctx;
}

function pushTestToDataLayer(abTest: AbTestData): void {
  getDataLayer().push({
    event: "Optimize Test",
    abTest: {
      experiment_id: abTest.id,
      experiment_variant: abTest.variantIndex,
      abt_experiment: abTest.group || null,
      abt_value: `${abTest.value}`,
    },
  });
}

/**
 * Returns data about the test that the user is currently in among the groups
 * specified for experimentName in {@link OPTIMIZE_EXPERIMENTS}
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const activeTest = useActiveTest("test");
 *
 *   const [isClientSide, setIsClientSide] = useState<boolean>(false);
 *   useEffect(() => setIsClientSide(true), []);
 *   if (!isClientSide) return null;
 *
 *   return (
 *     <button className={{ activeTest?.group == "test.red" ? "red" : "blue" }} />
 *   );
 * };
 * ```
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const activeTest = useActiveTest("test");
 *
 *   useEffect(() => {
 *     if (activeTest?.group === "test.variant") {
 *       // Some side-effect
 *     }
 *   }, [activeTest]);
 *
 *   return <ChildComponent />;
 * };
 * ```
 */
export const useActiveTest = (
  experimentName: keyof typeof OPTIMIZE_EXPERIMENTS
): AbTestData | null => {
  const context = useContext(AbTestContext);
  const { query } = useRouter() || {};
  const [abTest, setAbTest] = useState<AbTestData | null>(
    context?.abTest || null
  );

  useEffect(() => {
    if (!abTest) {
      const activeTest = determineActiveTest(experimentName, { query });
      if (activeTest?.experiment === experimentName) {
        setAbTest(activeTest);
        pushTestToDataLayer(activeTest);
      }
    }
  }, [abTest, experimentName, query]);

  return abTest || null;
};

/**
 * Return a human readable variant name of the the test that the user is
 * currently in, among the groups specified for experimentName, and pushes
 * an "Optimize Event" to the dataLayer
 *
 * This function is merely a convenience wrapper for {@link useActiveTest}
 *
 * @returns The active test group as ${experimentName}.${variant} or null
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const activeTestGroup = useActiveTestGroup("test");
 *
 *   const [isClientSide, setIsClientSide] = useState<boolean>(false);
 *   useEffect(() => setIsClientSide(true), []);
 *   if (!isClientSide) return null;
 *
 *   return (
 *     <button className={{ activeTest?.group == "test.red" ? "red" : "blue" }} />
 *   );
 * };
 * ```
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const activeTestGroup = useActiveTestGroup("test");
 *
 *   useEffect(() => {
 *     if (activeTestGroup === "test.variant") {
 *       // Some side-effect
 *     }
 *   }, [activeTestGroup]);
 *
 *   return <ChildComponent />;
 * };
 * ```
 */
export const useActiveTestGroup = (
  experimentName: keyof typeof OPTIMIZE_EXPERIMENTS
): string | null => {
  const activeTest = useActiveTest(experimentName);
  return activeTest?.group || null;
};

/**
 * @returns A function that accepts an app or page component and returns a
 * wrapped version of that component which is able to then access the current
 * AB test via {@link useActiveTestGroup} or from Component.getInitialProps().
 * This wrapping component ensures the experiment is pushed to the dataLayer
 * and that the necessary Vary headers are returned in the response.
 *
 * @example
 * ```tsx
 * const PageComponent = () => {
 *   const activeTestGroup = useActiveTestGroup("ssr-test-2");
 *   if (activeTestGroup === "ssr-test-2.variant") {
 *     return <VariantComponent />;
 *   } else {
 *     return <ControlComponent />;
 *   }
 * };
 *
 * PageComponent.getInitialProps = async ({ req, res, abTest }) => {
 *   if (abTest?.group == "useless-redirect.variant") {
 *     res.setHeader("Location", "/something-else/");
 *     res.statusCode = 302;
 *     res.end();
 *   }
 *  return {};
 * });
 *
 * export default withAbTest("useless-redirect", "ssr-test-2")(PageComponent);
 * ```
 */
export const withAbTest = <
  C extends AbTestAppPageContext,
  IP extends Record<string, unknown> & AbTestProps,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  P extends Record<string, any> & AbTestProps
>(
  ...experimentNames: ExperimentName[]
): {
  /**
   * A function that takes a React component and returns a wrapped version of
   * it that is able to access the AB test for the current request with
   * {@link useActiveTest} or as {@link AbTestData} in prop ``abTest`` passed
   * to its getInitialProps function.
   *
   * This wrapping component ensures the experiment is pushed to the dataLayer
   * and that the necessary Vary headers are returned in the response.
   */
  (Component: NextComponentType<C, IP, P>): NextComponentType<C, IP, P>;
} => {
  const buckets = getBucketsForExperiments(experimentNames);

  return (Component) => {
    const WithAbTest: NextComponentType<C, IP, P> = ({
      abTest,
      ...props
    }: P) => {
      useEffect(() => {
        experimentNames.forEach((experimentName) => {
          if (abTest?.experiment === experimentName) {
            pushTestToDataLayer(abTest);
          }
        });
      }, [abTest]);

      return (
        <AbTestContext.Provider value={{ abTest }}>
          <Component {...(props as P)} />
        </AbTestContext.Provider>
      );
    };

    // Set the displayName to indicate use of withAbTest (for debugging).
    WithAbTest.displayName = `withAbTest(${
      Component.displayName || "Component"
    })`;

    WithAbTest.getInitialProps = async (nextAppPageContext: C) => {
      const ctx = getNextPageContext(nextAppPageContext);
      const abTest = experimentNames
        .map((experimentName) => determineActiveTest(experimentName, ctx))
        .find((t) => t);

      ctx.abTest = abTest;

      const wrappedProps = (Component.getInitialProps
        ? await Component.getInitialProps(nextAppPageContext)
        : {}) as IP;

      const vary = buckets.map((bucketNum) => `x-test-bucket-${bucketNum}`);
      appendVaryHeaders(ctx.res, vary);
      return { ...wrappedProps, abTest };
    };
    return WithAbTest;
  };
};

export { OPTIMIZE_EXPERIMENTS, OPTIMIZE_BUCKETS };

export type { AbTestData, ExperimentName };

declare module "next" {
  export interface NextPageContext {
    abTest?: AbTestData;
  }
}
